// src/pages/ConfigurationsPage.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styles from './ConfigurationsPage.module.css';
import apiClient from '../services/ApiClient';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { toast } from 'react-toastify';

const REQUIRED_POPULAR_COUNTRIES = 8; // Yeni sabitimiz
const REQUIRED_POPULAR_REGIONS = 4; // Yeni sabitimiz
const MIN_REQUIRED_BANNERS = 1; // Yeni sabit
const REQUIRED_FEATURED_PACKAGES = 4; // Yeni sabit

const transformAllCountriesData = (apiCountriesData) => {
  if (!Array.isArray(apiCountriesData)) {
    return [];
  }

  return apiCountriesData.map((country, index) => ({
    id: country.id, // veya country.country kullanabilirsiniz
    name: country.country,
    flag: country.flag,
    originalDataId: country.originalDataId
  }));
};

const transformAllRegionsData = (apiRegionsData) => {
  if (!Array.isArray(apiRegionsData)) {
    return [];
  }

  return apiRegionsData.map((region, index) => ({
    id: region.id, // veya country.country kullanabilirsiniz
    name: region.region,
    flag: region.flag,
    originalDataId: region.originalDataId
  }));
};

const transformPackagesData = (apiPackages) => {
  const packages = apiPackages.products[0].refills
  const bundleId = apiPackages.products[0].originalId
  const countryName = apiPackages.country

  return packages.map(pkg => ({
    id: String(pkg.id),
    name: pkg.title,
    bundleId: bundleId,
    countryName: countryName,
    flag: apiPackages.flag
  }));
};

const ConfigurationsPage = () => {
  const [allAvailableCountries, setAllAvailableCountries] = useState([]);
  const [allAvailableRegions, setAllAvailableRegions] = useState([]);
  const [popularCountries, setPopularCountries] = useState([]);
  const [popularRegions, setPopularRegions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  // Kayıt yazıldı ama seçilen paketin açık planı yok: mobil ana sayfada fiyat "N/A".
  // 200 ile geldiği için toast'a bırakılmıyor; bir sonraki kayda kadar ekranda kalır.
  const [unsellableCountries, setUnsellableCountries] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSavingCountries, setIsSavingCountries] = useState(false); // Ülke kaydetme durumu
  const [isSavingRegions, setIsSavingRegions] = useState(false); // Ülke kaydetme durumu
  const [isSavingBanners, setIsSavingBanners] = useState(false); // Banner kaydetme durumu

  const [currentBanners, setCurrentBanners] = useState([]); // API'den gelen mevcut banner'lar { id, url }
  const [newBannerFiles, setNewBannerFiles] = useState([]); // Yüklenecek yeni dosya objeleri ve önizlemeleri
  const [bannerError, setBannerError] = useState(null); // Banner işlemleri için hata mesajı

  const [allRegionsOrCountriesForPackages, setAllRegionsOrCountriesForPackages] = useState([]); // Paket seçimi için ülke/bölge listesi
  const [selectedRegionForPackages, setSelectedRegionForPackages] = useState(''); // Paketleri listelemek için seçilen bölge/ülke ID'si
  const [availablePackagesInRegion, setAvailablePackagesInRegion] = useState([]); // <<< BURADA TANIMLI
  const [featuredPackages, setFeaturedPackages] = useState([]); // Öne çıkan olarak seçilmiş paketler
  const [isLoadingPackages, setIsLoadingPackages] = useState(false); // Bölgeye ait paketleri yüklerken
  const [isLoadingRegions, setIsLoadingRegions] = useState(true); // Bölge listesini yüklerken
  const [featuredPackagesError, setFeaturedPackagesError] = useState(null);
  const [isSavingFeaturedPackages, setIsSavingFeaturedPackages] = useState(false);

  const fetchData = useCallback(async () => {
      setIsLoading(true);
      setIsLoadingRegions(true); // Bölgeler için yüklemeyi başlat
      setError(null);
      setFeaturedPackagesError(null);
      setBannerError(null);

      try {
        const countriesResponse = await apiClient.get('/countries-data-light');
        if (countriesResponse.data && typeof countriesResponse.data.data === 'object') {
          const transformedCountries = transformAllCountriesData(countriesResponse.data.data);
          setAllAvailableCountries(transformedCountries);

          const popularResponse = await apiClient.get('/configurations/popular-countries');
          if (popularResponse.data && Array.isArray(popularResponse.data.data)) {
            const popularCountryObjects = transformPopularCountriesData(
              popularResponse.data.data
            );
            setPopularCountries(popularCountryObjects);
          } else {
            setPopularCountries([]);
            console.warn("API'den popüler ülke ID'leri (data.data) beklenen formatta gelmedi veya boş:", popularResponse.data);
          }
        } else {
          setAllAvailableCountries([]);
          console.error("API'den tüm ülkeler listesi beklenen formatta gelmedi (data.data objesi yok):", countriesResponse.data);
          throw new Error("Kullanılabilir ülke listesi yüklenemedi.");
        }

        const regionsResponse = await apiClient.get('/regions-data-light');
        if (regionsResponse.data && typeof regionsResponse.data.data === 'object') {
          const transformedRegions = transformAllRegionsData(regionsResponse.data.data);
          setAllAvailableRegions(transformedRegions);

          const popularResponse = await apiClient.get('/configurations/popular-regions');
          if (popularResponse.data && Array.isArray(popularResponse.data.data)) {
            const popularRegionObjects = transformPopularRegionsData(
              popularResponse.data.data
            );
            setPopularRegions(popularRegionObjects);
          } else {
            setPopularRegions([]);
            console.warn("API'den popüler bölge ID'leri (data.data) beklenen formatta gelmedi veya boş:", regionsResponse.data);
          }
        } else {
          setAllAvailableRegions([]);
          console.error("API'den tüm bölgeler listesi beklenen formatta gelmedi (data.data objesi yok):", regionsResponse.data);
          throw new Error("Kullanılabilir ülke listesi yüklenemedi.");
        }

        const bannersResponse = await apiClient.get('/configurations/banners');
        if (bannersResponse.data && Array.isArray(bannersResponse.data.data)) {
          // API'nızın { id, url } veya benzeri bir yapıda döndüğünü varsayalım
          setCurrentBanners(bannersResponse.data.data);
        } else {
          setCurrentBanners([]);
          console.warn("API'den mevcut banner'lar beklenen formatta gelmedi veya boş:", bannersResponse.data);
        }

        const regionsForPackagesRes = await apiClient.get('/regions-for-packages');
        if (regionsForPackagesRes.data && Array.isArray(regionsForPackagesRes.data.data)) {
          setAllRegionsOrCountriesForPackages(regionsForPackagesRes.data.data);
        } else {
          setAllRegionsOrCountriesForPackages([]);
          console.warn("Paket seçimi için bölgeler/ülkeler beklenen formatta gelmedi.");
        }
        setIsLoadingRegions(false); // Bölgeler yüklendi

        const currentFeaturedPackagesRes = await apiClient.get('/configurations/featured-packages');
        if (currentFeaturedPackagesRes.data && Array.isArray(currentFeaturedPackagesRes.data.data)) {
          const transformedFeatured = currentFeaturedPackagesRes.data.data.map(pkg => ({
            id: String(pkg.packageId),
            name: pkg.packageName,
            bundleId: pkg.packageBundleId, // API GET'te packageBundleId, PUT'ta bundleId adıyla gidiyor
            displayOrder: pkg.displayOrder,
            flag: pkg.flag,
            countryName: pkg.countryName
            // API'den bayrak/resim vs. geliyorsa buraya ekleyin
            // Şimdilik basit tutuyoruz.
          })).sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)); // displayOrder'a göre sırala
          setFeaturedPackages(transformedFeatured);
        } else {
          setFeaturedPackages([]);
        }
      } catch (err) {
        setError(err.message || "Veriler yüklenirken bir sorun oluştu. Lütfen daha sonra tekrar deneyin.");
        setBannerError(err.message || "Veriler yüklenirken bir sorun oluştu. Lütfen daha sonra tekrar deneyin."); // Banner için de aynı hata gösterilebilir
        setFeaturedPackagesError(err.message || "Veriler yüklenirken bir sorun oluştu. Lütfen daha sonra tekrar deneyin.");
        setAllAvailableCountries([]);
        setPopularCountries([]);
        setCurrentBanners([]);
      } finally {
        setIsLoading(false);
        setIsLoadingRegions(false); // Bölgeler için yükleme de bitti (hata olsa bile)
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const transformPopularCountriesData = (popularApiData) => {
    if (!popularApiData || !Array.isArray(popularApiData)) {
      return [];
    }
    return popularApiData.map(popCountry => {
      const idStr = String(popCountry.countryId);
      return {
        id: idStr,
        name: popCountry.countryName,
      };
    }).filter(Boolean); // Geçersiz girişleri filtrele
  };

  const transformPopularRegionsData = (popularApiData) => {
    if (!popularApiData || !Array.isArray(popularApiData)) {
      return [];
    }
    return popularApiData.map(popRegion => {
      const idStr = String(popRegion.regionId);
      return {
        id: idStr,
        name: popRegion.regionName,
      };
    }).filter(Boolean); // Geçersiz girişleri filtrele
  };

  const moveCountryUp = (index) => {
    if (index === 0) return; // Zaten en üstte
    const newPopularCountries = [...popularCountries];
    const temp = newPopularCountries[index];
    newPopularCountries[index] = newPopularCountries[index - 1];
    newPopularCountries[index - 1] = temp;
    setPopularCountries(newPopularCountries);
  };

  const moveCountryDown = (index) => {
    if (index === popularCountries.length - 1) return; // Zaten en altta
    const newPopularCountries = [...popularCountries];
    const temp = newPopularCountries[index];
    newPopularCountries[index] = newPopularCountries[index + 1];
    newPopularCountries[index + 1] = temp;
    setPopularCountries(newPopularCountries);
  };

  const moveRegionUp = (index) => {
    if (index === 0) return; // Zaten en üstte
    const newPopularRegions = [...popularRegions];
    const temp = newPopularRegions[index];
    newPopularRegions[index] = newPopularRegions[index - 1];
    newPopularRegions[index - 1] = temp;
    setPopularRegions(newPopularRegions);
  };

  const moveRegionDown = (index) => {
    if (index === popularRegions.length - 1) return; // Zaten en altta
    const newPopularRegions = [...popularRegions];
    const temp = newPopularRegions[index];
    newPopularRegions[index] = newPopularRegions[index + 1];
    newPopularRegions[index + 1] = temp;
    setPopularCountries(newPopularRegions);
  };


  const handleCountrySelect = (country) => {
    // Eğer zaten 8 ülke seçiliyse ve bu ülke zaten seçili değilse, ekleme
    if (popularCountries.length >= REQUIRED_POPULAR_COUNTRIES && !popularCountries.find(c => c.id === country.id)) {
      toast.error(`Tam olarak ${REQUIRED_POPULAR_COUNTRIES} popüler ülke seçili olmalıdır. Yeni bir ülke eklemek için önce mevcut bir seçimi kaldırın.`);
      return;
    }
    // Eğer ülke zaten seçili değilse ve 8'den az seçiliyse ekle
    if (!popularCountries.find(c => c.id === country.id)) {
      setPopularCountries(prev => [...prev, country]);
    }
  };

  const handleRegionSelect = (region) => {
    // Eğer zaten 8 ülke seçiliyse ve bu ülke zaten seçili değilse, ekleme
    if (popularRegions.length >= REQUIRED_POPULAR_REGIONS && !popularRegions.find(c => c.id === region.id)) {
      toast.error(`Tam olarak ${REQUIRED_POPULAR_REGIONS} popüler bölge seçili olmalıdır. Yeni bir bölge eklemek için önce mevcut bir seçimi kaldırın.`);
      return;
    }
    // Eğer ülke zaten seçili değilse ve 8'den az seçiliyse ekle
    if (!popularRegions.find(c => c.id === region.id)) {
      setPopularRegions(prev => [...prev, region]);
    }
  };

  const handleRemovePopularCountry = (countryId) => {
    setPopularCountries(prev => prev.filter(c => c.id !== countryId));
  };

  const handleRemovePopularRegion = (regionId) => {
    setPopularRegions(prev => prev.filter(c => c.id !== regionId));
  };

  const handleSavePopularCountries = async () => { /* handleSaveChanges -> handleSavePopularCountries oldu */
    if (popularCountries.length !== REQUIRED_POPULAR_COUNTRIES) {
      setError(`Lütfen tam olarak ${REQUIRED_POPULAR_COUNTRIES} popüler ülke seçin.`);
      return;
    }
    setIsSavingCountries(true);
    setError(null);
    setUnsellableCountries([]);
    const popularCountriesForApi = popularCountries.map((country, index) => ({
      countryId: parseInt(country.id, 10),
      originalDataId: country.originalDataId,
      countryName: country.name,
      displayOrder: index + 1,
      flag: country.flag
    }));
    try {
      const response = await apiClient.put('/configurations/popular-countries', { popularCountries: popularCountriesForApi });

      // Yeni sözleşme: liste artık BaseResponse -> data.popularCountries altında dönüyor.
      const responseData = response.data?.data;
      if (responseData && Array.isArray(responseData.popularCountries)) {
        setPopularCountries(transformPopularCountriesData(responseData.popularCountries));
      }

      // Eşleşmeyen isimler: country_coverage'da bulunamadı (originalDataId=0 / boş flag ile kaydedildi).
      const unmatched = responseData?.unmatched;
      if (Array.isArray(unmatched) && unmatched.length > 0) {
        toast.warn(`Kapsama bulunamayan ülkeler (originalDataId=0 / bayraksız kaydedildi): ${unmatched.join(", ")}`);
      }

      const unsellable = responseData?.unsellable;
      if (Array.isArray(unsellable) && unsellable.length > 0) {
        setUnsellableCountries(unsellable);
        toast.warn("Kaydedildi, ancak bazı ülkelerin satılabilir paketi yok. Ayrıntılar sayfada.");
      } else {
        toast.success("Popüler ülke ayarları başarıyla kaydedildi!");
      }
    } catch (err) {
      console.error("Popüler ülkeler kaydedilirken hata:", err);
      // 400 durumunda gövde düz metin (plain-text) döner, JSON değil.
      const data = err.response?.data;
      const message =
        typeof data === "string" ? data : (data?.message || "Popüler ülke ayarları kaydedilirken bir hata oluştu.");
      setError(message);
    } finally {
      setIsSavingCountries(false);
    }
  };

  const handleSavePopularRegions = async () => { /* handleSaveChanges -> handleSavePopularCountries oldu */
    if (popularRegions.length !== REQUIRED_POPULAR_REGIONS) {
      setError(`Lütfen tam olarak ${REQUIRED_POPULAR_REGIONS} popüler bölge seçin.`);
      return;
    }
    setIsSavingRegions(true);
    setError(null);
    const popularRegionsForApi = popularRegions.map((region, index) => ({
      regionId: parseInt(region.id, 10),
      regionName: region.name,
      displayOrder: index + 1,
      flag: region.flag,
      originalDataId: region.originalDataId
    }));

    try {
      await apiClient.put('/configurations/popular-regions', { popularRegions: popularRegionsForApi });
      toast.success("Popüler bölge ayarları başarıyla kaydedildi!");
    } catch (err) {
      console.error("Popüler bölgeler kaydedilirken hata:", err);
      setError(err.response?.data?.message || "Popüler bölge ayarları kaydedilirken bir hata oluştu.");
    } finally {
      setIsSavingRegions(false);
    }
  };
  const handleBannerFileChange = (event) => {
    const files = Array.from(event.target.files);
    const newFilesWithPreview = files.map(file => ({
      file: file,
      preview: URL.createObjectURL(file), // Önizleme için URL oluştur
      id: `new-${Date.now()}-${Math.random()}` // Geçici benzersiz ID
    }));
    setNewBannerFiles(prev => [...prev, ...newFilesWithPreview]);
    setBannerError(null); // Hata varsa temizle
    event.target.value = null; // Aynı dosyayı tekrar seçebilmek için input'u sıfırla
  };

  const removeNewBanner = (tempId) => {
    setNewBannerFiles(prev => prev.filter(item => item.id !== tempId));
  };

  const handleSaveBanners = async () => {
    // Mevcut banner'lar + yeni eklenecekler >= MIN_REQUIRED_BANNERS olmalı
    if (currentBanners.length + newBannerFiles.length < MIN_REQUIRED_BANNERS) {
      setBannerError(`En az ${MIN_REQUIRED_BANNERS} banner yüklü olmalıdır.`);
      return;
    }
    if (newBannerFiles.length === 0) {
      setBannerError("Kaydedilecek yeni banner seçilmedi. Mevcutları silmek için aşağıdaki listeden işlem yapın.");
      // Veya sadece mevcutları güncelleme gibi bir endpoint varsa o çağrılabilir.
      // Şimdilik yeni banner olmadan kaydetmeyi engelliyoruz.
      return;
    }

    setIsSavingBanners(true);
    setBannerError(null);

    const formData = new FormData();
    newBannerFiles.forEach(item => {
      formData.append('banners', item.file); // 'banners' API'nizin beklediği alan adı
    });

    try {
      // GERÇEK API ENDPOINT'İNİZİ KULLANIN (POST veya PUT)
      const response = await apiClient.post('/configurations/banners/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data' // Dosya yükleme için önemli
        }
      });
      toast.success("Bannerlar başarıyla yüklendi!");
      setNewBannerFiles([]); // Yüklenen dosyaları temizle
      // Başarılı yükleme sonrası mevcut banner listesini güncelle
      if (response.data && Array.isArray(response.data.data)) {
        setCurrentBanners(response.data.data); // API yeni listeyi dönüyorsa
      } else {
        // Veya listeyi yeniden çek
        // const bannersResponse = await apiClient.get('/configurations/banners');
        // setCurrentBanners(bannersResponse.data.data || []);
      }
    } catch (err) {
      console.error("Bannerlar yüklenirken hata:", err);
      setBannerError(err.response?.data?.message || "Bannerlar yüklenirken bir hata oluştu.");
    } finally {
      setIsSavingBanners(false);
    }
  };
  const handleDeleteExistingBanner = async (bannerId) => {
    if (currentBanners.length + newBannerFiles.length - 1 < MIN_REQUIRED_BANNERS) {
      toast.error(`Silme işlemi sonrası en az ${MIN_REQUIRED_BANNERS} banner kalmalıdır.`);
      return;
    }
    if (!window.confirm("Bu banner'ı silmek istediğinizden emin misiniz?")) return;

    // Optimistic UI: Önce arayüzden sil, sonra API'ye istek at
    // const bannerToRemove = currentBanners.find(b => b.id === bannerId);
    // setCurrentBanners(prev => prev.filter(b => b.id !== bannerId));

    try {
      // GERÇEK API ENDPOINT'İNİZİ KULLANIN (DELETE)
      await apiClient.delete(`/configurations/banners/${bannerId}`);
      toast.success("Banner başarıyla silindi.");
      // Başarılı silme sonrası listeyi yeniden çek veya state'i direkt güncelle
      setCurrentBanners(prev => prev.filter(b => b.id !== bannerId)); // En basit güncelleme
    } catch (err) {
      console.error("Banner silinirken hata:", err);
      toast.error("Banner silinirken bir hata oluştu.");
    }
  };

  useEffect(() => {
    if (!selectedRegionForPackages) {
      setAvailablePackagesInRegion([]); // Bölge seçilmemişse listeyi boşalt
      return;
    }
    const fetchPackagesByRegion = async () => {
      setIsLoadingPackages(true);
      setFeaturedPackagesError(null);
      try {
        const response = await apiClient.get(`/countries-data/${selectedRegionForPackages}`);
        if (response.data && Array.isArray(response.data.data)) {
          setAvailablePackagesInRegion(transformPackagesData(response.data.data[0]));
        } else {
          setAvailablePackagesInRegion([]);
          console.warn(`'${selectedRegionForPackages}' için paketler beklenen formatta gelmedi.`);
        }
      } catch (err) {
        console.error(`'${selectedRegionForPackages}' için paketler çekilirken hata:`, err);
        setFeaturedPackagesError(`Paketler yüklenirken bir sorun oluştu.`);
        setAvailablePackagesInRegion([]);
      } finally {
        setIsLoadingPackages(false);
      }
    };
    fetchPackagesByRegion();
  }, [selectedRegionForPackages]);

  const handlePackageSelectToFeature = (pkg) => {
    if (featuredPackages.length >= REQUIRED_FEATURED_PACKAGES && !featuredPackages.find(fp => fp.id === pkg.id)) {
      toast.error(`Tam olarak ${REQUIRED_FEATURED_PACKAGES} öne çıkan paket seçili olmalıdır. Yeni bir paket eklemek için önce mevcut bir seçimi kaldırın.`);
      return;
    }
    if (!featuredPackages.find(fp => fp.id === pkg.id)) {
      setFeaturedPackages(prev => [...prev, pkg]);
    }
  };

  const handleRemoveFeaturedPackage = (packageId) => {
    setFeaturedPackages(prev => prev.filter(fp => fp.id !== packageId));
  };

  const moveFeaturedPackageUp = (index) => {
    if (index === 0) return;
    const newPackages = [...featuredPackages];
    [newPackages[index - 1], newPackages[index]] = [newPackages[index], newPackages[index - 1]];
    setFeaturedPackages(newPackages);
  };

  const moveFeaturedPackageDown = (index) => {
    if (index === featuredPackages.length - 1) return;
    const newPackages = [...featuredPackages];
    [newPackages[index + 1], newPackages[index]] = [newPackages[index], newPackages[index + 1]];
    setFeaturedPackages(newPackages);
  };

  const handleSaveFeaturedPackages = async () => {
    if (featuredPackages.length !== REQUIRED_FEATURED_PACKAGES) {
      setFeaturedPackagesError(`Lütfen tam olarak ${REQUIRED_FEATURED_PACKAGES} öne çıkan paket seçin.`);
      return;
    }
    setIsSavingFeaturedPackages(true);
    setFeaturedPackagesError(null);

    const packagesForApi = featuredPackages.map((pkg, index) => ({
      packageId: parseInt(pkg.id, 10), // API sayısal ID bekliyorsa
      packageName: pkg.name, // API bu alanı da istiyorsa eklenebilir
      bundleId: pkg.bundleId,
      displayOrder: index + 1,
      flag: pkg.flag,
      countryName: pkg.countryName
    }));

    try {

      await apiClient.put('/configurations/featured-packages', { popularPackages: packagesForApi });
      toast.success("Öne çıkan paketler başarıyla güncellendi!");
    } catch (err) {
      console.error("Öne çıkan paketler kaydedilirken hata:", err);
      setFeaturedPackagesError(err.response?.data?.message || "Öne çıkan paketler kaydedilirken bir hata oluştu.");
    } finally {
      setIsSavingFeaturedPackages(false);
    }
  };

  const filteredSelectableCountries = useMemo(() => {
    return allAvailableCountries.filter(availCountry => {
      const isNotPopular = !popularCountries.find(popCountry => popCountry.id === availCountry.id);
      if (!isNotPopular) return false;
      if (searchTerm.trim() === '') return true;
      return availCountry.name.toLowerCase().includes(searchTerm.toLowerCase());
    });
  }, [allAvailableCountries, popularCountries, searchTerm]);

  const filteredSelectableRegions = useMemo(() => {
    return allAvailableRegions.filter(availRegion => {
      const isNotPopular = !popularRegions.find(popRegion => popRegion.id === availRegion.id);
      if (!isNotPopular) return false;
      if (searchTerm.trim() === '') return true;
      return availRegion.name.toLowerCase().includes(searchTerm.toLowerCase());
    });
  }, [allAvailableRegions, popularRegions, searchTerm]);

  const filteredAvailablePackages = useMemo(() => {
    // Öne çıkanlarda olmayan ve seçili bölgeye ait paketleri göster
    return availablePackagesInRegion.filter(
      availPkg => !featuredPackages.find(featPkg => featPkg.id === availPkg.id)
    );

  }, [availablePackagesInRegion, featuredPackages]);

  // Kaydetme butonu için bilgilendirme mesajı
  const getSaveButtonInfo = () => {
    if (popularCountries.length < REQUIRED_POPULAR_COUNTRIES) {
      return `Kaydetmek için ${REQUIRED_POPULAR_COUNTRIES - popularCountries.length} ülke daha seçin.`;
    }
    if (popularCountries.length > REQUIRED_POPULAR_COUNTRIES) {
      // Bu durum handleCountrySelect ile engellenmeli, ama bir fallback olarak
      return `Fazla ülke seçildi. Lütfen ${popularCountries.length - REQUIRED_POPULAR_COUNTRIES} ülke çıkarın.`;
    }
    return null; // Tam 8 ise mesaj yok
  };
  const saveButtonInfo = getSaveButtonInfo();

  const getSaveFeaturedPackagesButtonInfo = () => {
    if (featuredPackages.length < REQUIRED_FEATURED_PACKAGES) {
      return `Kaydetmek için ${REQUIRED_FEATURED_PACKAGES - featuredPackages.length} paket daha seçin.`;
    }
    return null;
  };
  const saveFeaturedPackagesButtonInfo = getSaveFeaturedPackagesButtonInfo();

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <LoadingSpinner />
          <p>Konfigürasyon verileri yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Genel Konfigürasyonlar</h1>
      </div>

      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>Popüler Ülkeler (Tam Olarak {REQUIRED_POPULAR_COUNTRIES})</h2>
        <p className={styles.sectionDescription}>
          Uygulama ana sayfasında veya ilgili bölümlerde öne çıkarılacak **tam olarak {REQUIRED_POPULAR_COUNTRIES} popüler ülke** seçmelisiniz.
          Seçim sıranız, görüntülenme sırasını etkileyebilir.
        </p>

        {error && (
          <div className={styles.errorPanel}>
            <span className={styles.errorPanelIcon}>⚠️</span>
            <div className={styles.errorPanelBody}>
              <p className={styles.errorPanelMessage}>{error}</p>
              <button className={styles.retryButton} onClick={fetchData} disabled={isLoading}>
                Tekrar dene
              </button>
            </div>
          </div>
        )}

        {unsellableCountries.length > 0 && (
          <div className={styles.warningPanel} role="status">
            <span className={styles.errorPanelIcon}>⚠️</span>
            <div className={styles.errorPanelBody}>
              <p className={styles.warningPanelTitle}>
                Kaydedildi, ancak {unsellableCountries.length === 1 ? 'bu ülkenin' : 'bu ülkelerin'} satılabilir paketi yok —
                mobil ana sayfada fiyat boş ("N/A") görünecek:
              </p>
              <ul className={styles.warningPanelList}>
                {unsellableCountries.map((item) => (
                  <li key={`${item.countryName}-${item.originalDataId}`}>
                    <strong>{item.countryName}</strong>
                    {item.bundleTitle && <> — {item.bundleTitle}</>}
                    {item.originalDataId != null && <span className="num"> (ref {item.originalDataId})</span>}
                    {item.reason && <>: {item.reason}</>}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {allAvailableCountries.length === 0 && !isLoading && !error && (
          <p className={styles.infoText}>Yüklenecek ülke bulunamadı. Lütfen API bağlantınızı kontrol edin.</p>
        )}

        {allAvailableCountries.length > 0 && (
          <div className={styles.popularCountriesContainer}>
            <div className={styles.selectedCountries}>
              <h3>Seçilen Popüler Ülkeler ({popularCountries.length}/{REQUIRED_POPULAR_COUNTRIES})</h3>
              {popularCountries.length === 0 && !isLoading && (
                <p className={styles.emptyState}>Henüz popüler ülke seçilmedi.</p>
              )}
              <ul className={styles.countryListSelected}>
                {popularCountries.map((country, index) => (
                  <li key={country.id} className={styles.countryItemPopular}>
                    <span className={styles.countryName}>{country.name}</span>
                    {/* --- YENİ: Sıralama Butonları --- */}
                    <div className={styles.orderButtons}>
                      <button
                        onClick={() => moveCountryUp(index)}
                        disabled={index === 0} // İlk eleman için yukarı butonu pasif
                        className={styles.orderButton}
                        title="Yukarı Taşı"
                      >
                        ▲ {/* Yukarı ok */}
                      </button>
                      <button
                        onClick={() => moveCountryDown(index)}
                        disabled={index === popularCountries.length - 1} // Son eleman için aşağı butonu pasif
                        className={styles.orderButton}
                        title="Aşağı Taşı"
                      >
                        ▼ {/* Aşağı ok */}
                      </button>
                    </div>
                    {/* --- /YENİ: Sıralama Butonları --- */}
                    <button
                      onClick={() => handleRemovePopularCountry(country.id)}
                      className={styles.removeButton}
                      title="Kaldır"
                    >
                      ×
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div className={styles.availableCountries}>
              <h3>Eklenebilecek Ülkeler</h3>
              <input
                type="text"
                placeholder="Ülke ara..."
                className={styles.searchInput}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />

              {filteredSelectableCountries.length === 0 && popularCountries.length >= REQUIRED_POPULAR_COUNTRIES && (
                <p className={styles.infoText}>Seçim limiti ({REQUIRED_POPULAR_COUNTRIES} ülke) doldu. Yeni eklemek için önce birini kaldırın.</p>
              )}
              {filteredSelectableCountries.length === 0 && popularCountries.length < REQUIRED_POPULAR_COUNTRIES && searchTerm && (
                <p className={styles.infoText}>"{searchTerm}" ile eşleşen ülke bulunamadı.</p>
              )}
              {filteredSelectableCountries.length === 0 && popularCountries.length < REQUIRED_POPULAR_COUNTRIES && !searchTerm && !isLoading && (
                <p className={styles.infoText}>Eklenebilecek başka ülke bulunmuyor veya hepsi seçilmiş.</p>
              )}

              <ul className={styles.countryListAvailable}>
                {filteredSelectableCountries.map((country) => (
                  <li
                    key={country.id}
                    className={`${styles.countryItemAvailable} ${popularCountries.length >= REQUIRED_POPULAR_COUNTRIES ? styles.disabled : ''}`}
                    onClick={() => handleCountrySelect(country)} // handleCountrySelect kontrolü yapacak
                    title={popularCountries.length >= REQUIRED_POPULAR_COUNTRIES ? `Tam olarak ${REQUIRED_POPULAR_COUNTRIES} ülke seçili olmalıdır.` : "Popülerlere Ekle"}
                  >
                    <span className={styles.countryName}>{country.name}</span>
                    {popularCountries.length < REQUIRED_POPULAR_COUNTRIES && (
                      <span className={styles.addButton}>+</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {allAvailableCountries.length > 0 && (
          <div className={styles.actions}>
            {saveButtonInfo && <p className={styles.saveButtonInfo}>{saveButtonInfo}</p>}
            <button
              onClick={handleSavePopularCountries}
              className={styles.saveButton}
              disabled={isSavingCountries || isLoading || popularCountries.length !== REQUIRED_POPULAR_COUNTRIES}
            >
              {isSavingCountries ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
            </button>
          </div>
        )}
      </div>
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>Popüler Bölgeler (Tam Olarak {REQUIRED_POPULAR_REGIONS})</h2>
        <p className={styles.sectionDescription}>
          Uygulama ana sayfasında veya ilgili bölümlerde öne çıkarılacak **tam olarak {REQUIRED_POPULAR_REGIONS} popüler bölge** seçmelisiniz.
          Seçim sıranız, görüntülenme sırasını etkileyebilir.
        </p>

        {error && (
          <div className={styles.errorPanel}>
            <span className={styles.errorPanelIcon}>⚠️</span>
            <div className={styles.errorPanelBody}>
              <p className={styles.errorPanelMessage}>{error}</p>
              <button className={styles.retryButton} onClick={fetchData} disabled={isLoading}>
                Tekrar dene
              </button>
            </div>
          </div>
        )}

        {allAvailableRegions.length === 0 && !isLoading && !error && (
          <p className={styles.infoText}>Yüklenecek bölge bulunamadı. Lütfen API bağlantınızı kontrol edin.</p>
        )}

        {allAvailableRegions.length > 0 && (
          <div className={styles.popularCountriesContainer}>
            <div className={styles.selectedCountries}>
              <h3>Seçilen Popüler Bölgeler ({popularRegions.length}/{REQUIRED_POPULAR_REGIONS})</h3>
              {popularRegions.length === 0 && !isLoading && (
                <p className={styles.emptyState}>Henüz popüler bölger seçilmedi.</p>
              )}
              <ul className={styles.countryListSelected}>
                {popularRegions.map((region, index) => (
                  <li key={region.id} className={styles.countryItemPopular}>
                    <span className={styles.countryName}>{region.name}</span>
                    {/* --- YENİ: Sıralama Butonları --- */}
                    <div className={styles.orderButtons}>
                      <button
                        onClick={() => moveRegionUp(index)}
                        disabled={index === 0} // İlk eleman için yukarı butonu pasif
                        className={styles.orderButton}
                        title="Yukarı Taşı"
                      >
                        ▲ {/* Yukarı ok */}
                      </button>
                      <button
                        onClick={() => moveRegionDown(index)}
                        disabled={index === popularRegions.length - 1} // Son eleman için aşağı butonu pasif
                        className={styles.orderButton}
                        title="Aşağı Taşı"
                      >
                        ▼ {/* Aşağı ok */}
                      </button>
                    </div>
                    {/* --- /YENİ: Sıralama Butonları --- */}
                    <button
                      onClick={() => handleRemovePopularRegion(region.id)}
                      className={styles.removeButton}
                      title="Kaldır"
                    >
                      ×
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div className={styles.availableCountries}>
              <h3>Eklenebilecek Bölgeler</h3>
              <input
                type="text"
                placeholder="Bölge ara..."
                className={styles.searchInput}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />

              {filteredSelectableRegions.length === 0 && popularRegions.length >= REQUIRED_POPULAR_REGIONS && (
                <p className={styles.infoText}>Seçim limiti ({REQUIRED_POPULAR_REGIONS} bölge) doldu. Yeni eklemek için önce birini kaldırın.</p>
              )}
              {filteredSelectableRegions.length === 0 && popularRegions.length < REQUIRED_POPULAR_REGIONS && searchTerm && (
                <p className={styles.infoText}>"{searchTerm}" ile eşleşen bölge bulunamadı.</p>
              )}
              {filteredSelectableRegions.length === 0 && popularRegions.length < REQUIRED_POPULAR_REGIONS && !searchTerm && !isLoading && (
                <p className={styles.infoText}>Eklenebilecek başka bölge bulunmuyor veya hepsi seçilmiş.</p>
              )}

              <ul className={styles.countryListAvailable}>
                {filteredSelectableRegions.map((region) => (
                  <li
                    key={region.id}
                    className={`${styles.countryItemAvailable} ${popularRegions.length >= REQUIRED_POPULAR_REGIONS ? styles.disabled : ''}`}
                    onClick={() => handleRegionSelect(region)} // handleCountrySelect kontrolü yapacak
                    title={popularRegions.length >= REQUIRED_POPULAR_REGIONS ? `Tam olarak ${REQUIRED_POPULAR_REGIONS} bölge seçili olmalıdır.` : "Popülerlere Ekle"}
                  >
                    <span className={styles.countryName}>{region.name}</span>
                    {popularRegions.length < REQUIRED_POPULAR_REGIONS && (
                      <span className={styles.addButton}>+</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {allAvailableRegions.length > 0 && (
          <div className={styles.actions}>
            {saveButtonInfo && <p className={styles.saveButtonInfo}>{saveButtonInfo}</p>}
            <button
              onClick={handleSavePopularRegions}
              className={styles.saveButton}
              disabled={isSavingRegions || isLoading || popularRegions.length !== REQUIRED_POPULAR_REGIONS}
            >
              {isSavingRegions ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
            </button>
          </div>
        )}
      </div>
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>Banner Yönetimi (En Az {MIN_REQUIRED_BANNERS} Adet)</h2>
        <p className={styles.sectionDescription}>
          Uygulama içinde gösterilecek banner resimlerini yönetin.
        </p>

        {bannerError && (
          <div className={styles.errorPanel}>
            <span className={styles.errorPanelIcon}>⚠️</span>
            <div className={styles.errorPanelBody}>
              <p className={styles.errorPanelMessage}>{bannerError}</p>
              <button className={styles.retryButton} onClick={fetchData} disabled={isLoading}>
                Tekrar dene
              </button>
            </div>
          </div>
        )}

        <div className={styles.bannerUploadSection}>
          <h3>Yeni Banner Ekle</h3>
          <input
            type="file"
            accept="image/png, image/jpeg, image/gif, image/webp" // Kabul edilen dosya türleri
            multiple // Birden fazla dosya seçimine izin ver
            onChange={handleBannerFileChange}
            className={styles.fileInput}
            id="banner-upload"
          />
          <label htmlFor="banner-upload" className={styles.fileInputLabel}>
            Resim Seç
          </label>

          {newBannerFiles.length > 0 && (
            <div className={styles.newBannersPreviewContainer}>
              <h4>Seçilen Yeni Bannerlar (Yüklenecek):</h4>
              <ul className={styles.bannerList}>
                {newBannerFiles.map(item => (
                  <li key={item.id} className={styles.bannerItemNew}>
                    <img src={item.preview} alt={`Yeni Banner Önizleme ${item.file.name}`} className={styles.bannerPreviewImage} />
                    <span className={styles.bannerFileName}>{item.file.name} ({(item.file.size / 1024).toFixed(1)} KB)</span>
                    <button onClick={() => removeNewBanner(item.id)} className={styles.removeButtonSmall} title="Bu seçimi kaldır">
                      ×
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className={styles.currentBannersSection}>
          <h3>Mevcut Bannerlar</h3>
          {currentBanners.length === 0 && !isLoading && (
            <p className={styles.emptyState}>Henüz yüklenmiş banner bulunmuyor.</p>
          )}
          <ul className={styles.bannerList}>
            {currentBanners.map(banner => (
              <li key={banner.id} className={styles.bannerItemExisting}>
                <img src={banner.imageUrl} alt={`Mevcut Banner ${banner.id}`} className={styles.bannerPreviewImage} />
                {/* API'den dosya adı geliyorsa gösterilebilir: <span className={styles.bannerFileName}>{banner.fileName}</span> */}
                <button onClick={() => handleDeleteExistingBanner(banner.id)} className={styles.removeButtonSmall} title="Bu banner'ı sil">
                  Sil
                </button>
              </li>
            ))}
          </ul>
        </div>


        <div className={styles.actions}>
          {/* Yeterli banner yoksa uyarı */}
          {(currentBanners.length + newBannerFiles.length < MIN_REQUIRED_BANNERS && !isSavingBanners) && (
            <p className={styles.saveButtonInfo}>
              Kaydetmek için en az {MIN_REQUIRED_BANNERS - (currentBanners.length + newBannerFiles.length)} banner daha ekleyin veya mevcutlardan daha az silin.
            </p>
          )}
          <button
            onClick={handleSaveBanners}
            className={styles.saveButton}
            // Kaydetme butonu; yeni dosya varsa VEYA mevcutları silme gibi bir durum varsa aktif olmalı.
            // Şimdilik sadece yeni dosya varsa aktif yapıyoruz.
            disabled={isSavingBanners || isLoading || newBannerFiles.length === 0 || (currentBanners.length + newBannerFiles.length < MIN_REQUIRED_BANNERS)}
          >
            {isSavingBanners ? 'Bannerlar Kaydediliyor...' : 'Yeni Bannerları Yükle/Kaydet'}
          </button>
        </div>
      </div>
      {/* --- YENİ: Öne Çıkan e-SIM Paketleri Bölümü --- */}
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>Öne Çıkan e-SIM Paketleri (Tam Olarak {REQUIRED_FEATURED_PACKAGES})</h2>
        <p className={styles.sectionDescription}>
          Uygulamada öne çıkarılacak **tam olarak {REQUIRED_FEATURED_PACKAGES} e-SIM paketi** seçin.
          Önce paketlerin listeleneceği bir ülke/bölge seçin.
        </p>

        {featuredPackagesError && (
          <div className={styles.errorPanel}>
            <span className={styles.errorPanelIcon}>⚠️</span>
            <div className={styles.errorPanelBody}>
              <p className={styles.errorPanelMessage}>{featuredPackagesError}</p>
            </div>
          </div>
        )}

        <div className={styles.packageSelectionContainer}>
          <div className={styles.regionSelectorGroup}>
            <label htmlFor="region-select-for-packages">Ülke/Bölge Seçin:</label>
            {isLoadingRegions ? <LoadingSpinner size="small" /> : (
              <select
                id="region-select-for-packages"
                value={selectedRegionForPackages}
                onChange={(e) => setSelectedRegionForPackages(e.target.value)}
                className={styles.regionSelect}
              >
                <option value="">-- Bir Bölge/Ülke Seçin --</option>
                {allRegionsOrCountriesForPackages.map(region => (
                  <option value={region.country}>{region.country}</option>
                ))};

              </select>
            )}
          </div>

          {/* Paket Listeleme ve Seçim Alanı */}
          <div className={styles.featuredPackagesInteractionArea}>
              <div className={styles.availablePackagesList}>
                {!selectedRegionForPackages ? (
                  <>
                    <h4>Bölgedeki Paketler</h4>
                    <p className={styles.emptyStateSmall}>Paketleri listelemek için yukarıdan bir ülke/bölge seçin.</p>
                  </>
                ) : (
                  <>
                    <h4>"{allRegionsOrCountriesForPackages.find(r => r.country === selectedRegionForPackages)?.country}" Bölgesindeki Paketler</h4>
                    {isLoadingPackages ? <LoadingSpinner /> : (
                      filteredAvailablePackages.length > 0 ? (
                        <ul>
                          {filteredAvailablePackages.map(pkg => (
                            <li key={pkg.id} className={`${styles.packageItemAvailable} ${featuredPackages.length >= REQUIRED_FEATURED_PACKAGES ? styles.disabled : ''}`}>
                              <span className={styles.packageLabel}>
                                {pkg.flag && <img src={pkg.flag} alt="" className={styles.packageFlag} />}
                                <span className={styles.packageName}>{pkg.name}</span>
                              </span>
                              {featuredPackages.length < REQUIRED_FEATURED_PACKAGES && (
                                <button onClick={() => handlePackageSelectToFeature(pkg)} className={styles.addPackageButton} title="Öne Çıkanlara Ekle">Ekle</button>
                              )}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className={styles.emptyStateSmall}>Bu bölgede eklenebilecek başka paket bulunmuyor veya hepsi seçilmiş.</p>
                      )
                    )}
                  </>
                )}
              </div>

              <div className={styles.selectedFeaturedPackages}>
                <h4>Öne Çıkan Paketler ({featuredPackages.length}/{REQUIRED_FEATURED_PACKAGES})</h4>
                {featuredPackages.length === 0 ? (
                  <p className={styles.emptyStateSmall}>Henüz öne çıkan paket seçilmedi.</p>
                ) : (
                  <ul>
                    {featuredPackages.map((pkg, index) => (
                      <li key={pkg.id} className={styles.packageItemFeatured}>
                        <span className={styles.packageLabel}>
                          {pkg.flag && <img src={pkg.flag} alt="" className={styles.packageFlag} />}
                          <span className={styles.packageName}>
                            {pkg.name}
                            {pkg.countryName && <em className={styles.packageCountry}>{pkg.countryName}</em>}
                          </span>
                        </span>
                        <div className={styles.orderButtonsPackages}>
                          <button onClick={() => moveFeaturedPackageUp(index)} disabled={index === 0}>▲</button>
                          <button onClick={() => moveFeaturedPackageDown(index)} disabled={index === featuredPackages.length - 1}>▼</button>
                        </div>
                        <button onClick={() => handleRemoveFeaturedPackage(pkg.id)} className={styles.removePackageButton}>×</button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
          </div>
        </div>

        <div className={styles.actions}>
          {saveFeaturedPackagesButtonInfo && <p className={styles.saveButtonInfo}>{saveFeaturedPackagesButtonInfo}</p>}
          <button
            onClick={handleSaveFeaturedPackages}
            className={styles.saveButton}
            disabled={isSavingFeaturedPackages || isLoading || featuredPackages.length !== REQUIRED_FEATURED_PACKAGES}
          >
            {isSavingFeaturedPackages ? 'Kaydediliyor...' : 'Öne Çıkan Paketleri Kaydet'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfigurationsPage;